import axios, { AxiosError, AxiosInstance } from "axios"
import { axiosInstance, axiosInstanceV1 } from "./services/v2/axiosInstance"

/*
	Un solo lugar que decide qué pasa cuando el servidor contesta 401.

	Antes cada pantalla lo tragaba a su manera: Horarios decía "La búsqueda no pudo
	completarse", Cerrar sesión decía "Las credenciales proporcionadas no son válidas"
	y dejaba el token muerto guardado, y la persona quedaba dando vueltas entre el error
	y el login sin saber por qué (videos de Torreón, 2026-09-29).

	Ahora: cualquier 401 con token → se borra la sesión de los tres lugares donde vive
	(localStorage.accessToken, persist:root y el estado en memoria), se guarda el motivo
	que mandó el servidor (sesion_cerrada / sesion_vencida / sesion_inactiva) y se va al
	login con una navegación completa. El login lo lee y lo muestra.

	El 401 del propio login (clave equivocada) y los de restablecer clave no cuentan.
*/
const MOTIVO_KEY = "wapp_motivo_salida"
const RUTAS_LIBRES = ["/access/_login", "/access/_pwdReset", "/access/_pwdResetConfirm"]
const MENSAJE_POR_DEFECTO = "Tu sesión ya no es válida. Vuelve a iniciar sesión."

let saliendo = false

const esRutaLibre = (url: string): boolean => RUTAS_LIBRES.some((ruta) => url.indexOf(ruta) >= 0)

const llevabaToken = (error: AxiosError): boolean => {
	const headers: any = error.config && error.config.headers
	const valor = headers && (headers.Authorization || headers.authorization)
	return typeof valor === "string" && valor.replace("Bearer", "").trim() !== ""
}

export const borrarSesionLocal = () => {
	try {
		localStorage.removeItem("accessToken")
		localStorage.removeItem("persist:root")
		localStorage.removeItem("currentRole")
	} catch (_) { /* sin almacenamiento: no hay nada que borrar */ }
}

export const guardarMotivoDeSalida = (mensaje: string) => {
	try {
		sessionStorage.setItem(MOTIVO_KEY, JSON.stringify({ mensaje, cuando: Date.now() }))
	} catch (_) { /* modo privado: se pierde el texto, no la salida */ }
}

/*
	Lo lee la pantalla de login. NO se borra al leer: cuando muere la sesión, la app
	vieja alcanza a navegar al login por su cuenta (y a leer el motivo) antes de que
	llegue la navegación completa; si se borrara en esa primera lectura, el login
	nuevo ya no tendría nada que mostrar. Se borra al entrar con éxito, y de todos
	modos caduca a los dos minutos.
*/
export const leerMotivoDeSalida = (): string | null => {
	try {
		const crudo = sessionStorage.getItem(MOTIVO_KEY)
		if (!crudo) {
			return null
		}
		const guardado = JSON.parse(crudo)
		if (!guardado || typeof guardado.mensaje !== "string" || Date.now() - Number(guardado.cuando || 0) > 2 * 60 * 1000) {
			sessionStorage.removeItem(MOTIVO_KEY)
			return null
		}
		return guardado.mensaje
	} catch (_) {
		return null
	}
}

export const olvidarMotivoDeSalida = () => {
	try {
		sessionStorage.removeItem(MOTIVO_KEY)
	} catch (_) { /* nada */ }
}

export const salirPorSesionInvalida = (mensaje?: string) => {
	if (saliendo) {
		return
	}
	saliendo = true
	borrarSesionLocal()
	guardarMotivoDeSalida(mensaje && mensaje.trim() !== "" ? mensaje : MENSAJE_POR_DEFECTO)

	if (window.location.pathname.indexOf("/access") === 0) {
		//Ya estamos en el login (sesión guardada que murió): basta con recargar limpio.
		window.location.reload()
		return
	}
	window.location.replace("/access")
}

const alFallar = (error: AxiosError) => {
	const respuesta: any = error && error.response
	const url = (error && error.config && error.config.url) || ""

	if (respuesta && respuesta.status === 401 && !esRutaLibre(url) && llevabaToken(error)) {
		const datos: any = respuesta.data
		salirPorSesionInvalida(datos && typeof datos.message === "string" ? datos.message : undefined)
	}
	return Promise.reject(error)
}

export const instalarGuardiaDeSesion = () => {
	const instancias: AxiosInstance[] = [axios, axiosInstance, axiosInstanceV1]
	instancias.forEach((instancia) => instancia.interceptors.response.use((r) => r, alFallar))
}
